/** @jsxRuntime classic */
/** @jsx jsx */
import { useState, useCallback, useContext } from 'react';
import { jsx, css } from '@emotion/react';
import { useTranslation } from 'react-i18next';

import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

import { useNPSActions } from '../../actions/NPSActions';

import AppContext from '../../contexts/app';
import { track } from '../../utils/track';

export default function NPSDialog() {
  const { t } = useTranslation();
  const { npsDialog, setNPSDialog } = useContext(AppContext);
  const [comment, setComment] = useState('');
  const { title, rating, trackingKey, checkKey, open } = npsDialog;
  const { setNPS } = useNPSActions();

  function setRating(value) {
    setNPSDialog({
      ...npsDialog,
      rating: value,
    });
  }

  const handleComment = useCallback((event) => {
    setComment(event.target.value);
  }, []);

  const handleClose = useCallback(() => {
    setNPSDialog({
      open: false,
      rating: null,
      type: null,
      trackingKey: null,
      checkKey: null,
    });
    setComment('');
  }, [setNPSDialog]);

  const handleSubmit = useCallback(() => {
    track(`nps:${trackingKey}`, { rating, comment });
    setNPS({
      rating,
      comment,
      key: checkKey,
      trackingKey,
    });
    handleClose();
  }, [rating, comment, trackingKey, checkKey, setNPS, handleClose]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>
        <div
          css={css`
            display: flex;
          `}
        >
          {title}
          <IconButton
            css={css`
              margin-left: auto;
              border: 1px solid #6c6c6c;
              border-radius: 0.5rem;
              position: relative;
            `}
            onClick={handleClose}
            size="small"
          >
            <CloseOutlinedIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: center;
          `}
        >
          {Array.from({ length: 5 }).map((_, index) => {
            const value = index + 1;
            const Icon = value <= rating ? StarIcon : StarBorderIcon;
            return (
              <Icon
                key={value}
                css={css`
                  cursor: pointer;
                  font-size: 2rem;
                  color: ${rating >= value ? '#FFC107' : '#E0E0E0'};
                  margin: 0 0.25rem;
                `}
                onClick={() => setRating(value)}
              />
            );
          })}
        </div>
        <TextField
          css={css`
            margin-top: 1rem;
          `}
          placeholder={t('Berätta mer (valfritt)')}
          value={comment}
          onChange={handleComment}
          fullWidth
          multiline
          rows={4}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handleSubmit}>
          {t('Skicka')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
